<template>
  <section class="mt-5 mb-5">
    <div class="container">
      <div class="row justify-content-center">
        <!-- Gate by access -->
        <div class="col-md-10" v-if="hasAlbumRights">

          <!-- Top bar -->
          <div class="page-bar py-3">
            <div class="container">
              <div class="d-flex align-items-center">
                <BackButton class="btn btn-link p-0 mr-2" fallback="/host">
                  <i class="fas fa-chevron-left"></i>
                </BackButton>
                <div class="page-title flex-grow-1">{{ eventTitle || 'Event' }}</div>

                <div class="dropdown" v-if="isMySource">
                  <button class="dot-btn btn" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                    <i class="fas fa-ellipsis-v"></i>
                  </button>
                  <div class="dropdown-menu dropdown-menu-right album-detail-menu">
                    <template v-if="isMySource">
                      <a class="dropdown-item album-detail-menu__item" href="#" @click.prevent="openViewPerms">
                        <i class="far fa-eye mr-2"></i> View Permission
                      </a>
                      <a class="dropdown-item album-detail-menu__item" href="#" @click.prevent="openUploadPerms">
                        <i class="fas fa-upload mr-2"></i> Upload Permission
                      </a>
                      <a class="dropdown-item album-detail-menu__item" href="#" @click.prevent="openShare">
                        <i class="fas fa-share-alt mr-2"></i> Share
                      </a>
                      <div class="dropdown-divider"></div>
                    </template>

                    <a class="dropdown-item album-detail-menu__item" href="#" @click.prevent="enterSelectMode">
                      <i class="far fa-check-square mr-2"></i>
                      {{ mode === 'select' ? 'Exit Select' : 'Select' }}
                    </a>
                    <a
                      v-if="mode === 'select'"
                      class="dropdown-item album-detail-menu__item album-detail-menu__item--danger"
                      href="#"
                      :class="{ disabled: selectedImages.length === 0 || deleting }"
                      @click.prevent="selectedImages.length && deleteSelected()"
                    >
                      <i class="far fa-trash-alt mr-2"></i>
                      Delete Selected ({{ selectedImages.length }})
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Header card -->
          <div class="detail-card detail-card--compact mb-3">
            <!-- Uploader -->
            <div class="detail-card__body" v-if="canUpload">
              <FileUploader
                :eventId="eventId"
                @refreshGallery="refreshAlbums(true)"
              />
            </div>
          </div>

          <!-- Gallery card -->
          <div class="detail-card mb-3">
            <div class="detail-card__head">
              <div class="gallery-header">
                <h4 class="gallery-title mb-0">{{ albums.length }} Photos</h4>
                <div class="gallery-toolbar">
                  <button
                    type="button"
                    class="toolbar-btn"
                    :class="{ active: mode === 'select' }"
                    @click="toggleSelectMode"
                  >
                    <i class="far fa-check-square mr-1"></i>
                    {{ mode === 'select' ? 'Exit Select' : 'Select' }}
                  </button>

                  <div
                    class="download-dropdown"
                    ref="downloadDropdownRef"
                  >
                    <button
                      type="button"
                      class="toolbar-btn toolbar-btn--muted"
                      :disabled="!albums.length || downloading"
                      :aria-expanded="showDownloadMenu.toString()"
                      @click.stop="toggleDownloadMenu"
                    >
                      <template v-if="!downloading">
                        <i class="fas fa-cloud-download-alt mr-1"></i>
                        Download
                      </template>
                      <template v-else>
                        <span class="spinner-border spinner-border-sm mr-1"></span>
                        Preparing…
                      </template>
                    </button>

                    <div
                      class="download-menu shadow-sm"
                      v-if="showDownloadMenu"
                      @click.stop
                    >
                      <div class="download-label">All photos</div>
                      <button
                        v-for="opt in qualityOptions"
                        :key="`all-${opt.key}`"
                        type="button"
                        class="download-menu-item"
                        @click="requestDownload('all', opt.key)"
                      >
                        {{ opt.label }}
                      </button>

                      <template v-if="hasSelectedItems">
                        <div class="download-label mt-2">Selected ({{ selectedImages.length }})</div>
                        <button
                          v-for="opt in qualityOptions"
                          :key="`selected-${opt.key}`"
                          type="button"
                          class="download-menu-item"
                          @click="requestDownload('selected', opt.key)"
                        >
                          {{ opt.label }}
                        </button>
                      </template>

                      <template v-else>
                        <div class="download-note">Select photos to enable bulk options.</div>
                      </template>
                    </div>
                  </div>

                  <button
                    v-if="mode === 'select' && selectedImages.length > 0"
                    class="toolbar-btn toolbar-btn--danger"
                    :disabled="deleting"
                    @click="deleteSelected"
                  >
                    <span v-if="!deleting">Delete Selected ({{ selectedImages.length }})</span>
                    <span v-else>
                      <span class="spinner-border spinner-border-sm mr-1"></span>Deleting…
                    </span>
                  </button>
                </div>
              </div>
            </div>

            <div class="detail-card__body">
              <div class="masonry" v-if="albums.length > 0" ref="galleryWrapperRef">
                <div
                  class="masonry-item"
                  v-for="(img, index) in albums"
                  :key="imgKey(img)"
                >
                  <div
                    class="imgBlock position-relative"
                    :class="{ 'selecting': mode==='select', 'selected': isKeySelected(imgKey(img)) }"
                    @click="onImageClick(img, $event, index)"
                  >
                    <label
                      v-if="mode === 'select' && deletableSet.has(imgKey(img))"
                      class="image-checkbox"
                    >
                      <input
                        type="checkbox"
                        :value="imgKey(img)"
                        v-model="selectedImages"
                        @click.stop
                      />
                      <span class="checkmark"></span>
                    </label>
                    <div
                      class="imgBlock-inner"
                      :data-src="img.image || img.thumb"
                      :data-sub-html="img.username ? `<h4>${img.username}</h4>` : ''"
                    >
                      <img class="album-img" v-no-right-click :src="img.thumb || img.image" alt="Album Image" />
                      <button
                        v-if="mode !== 'select'"
                        type="button"
                        class="imgBlock-overlay"
                        @click.stop="openLightbox(index)"
                      >
                        <i class="fas fa-search-plus"></i>
                      </button>
                    </div>
                    <h4><i class="fad fa-user mr-1 ml-1"></i> {{ img.username || 'User' }}</h4>
                  </div>
                </div>
              </div>

              <div class="text-center" v-else>
                <img v-no-right-click src="/assets/icons/no-images.svg" style="height:200px" alt="" class="img-fluid"> 
              </div>
            </div>
          </div>
        </div>

        <!-- Not allowed -->
        <div class="col-md-10" v-else-if="accessDenied">
          <div class="detail-card">
            <div class="detail-card__body text-center py-4">
              <i class="fas fa-user fa-3x mb-2"></i>
              <p>You do not have permission to view this album</p>
            </div>
          </div>
        </div>

        <!-- Pending access -->
        <div class="col-md-10" v-else>
          <div class="detail-card skeleton-card">
            <div class="detail-card__body">
              <div class="skeleton-line skeleton-line--lg mb-3"></div>
              <div class="skeleton-line skeleton-line--md mb-3"></div>
              <div class="skeleton-line skeleton-line--sm mb-4"></div>
              <div class="skeleton-block mb-3"></div>
              <div class="skeleton-line skeleton-line--sm mb-2"></div>
              <div class="skeleton-line skeleton-line--md"></div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- New modular modals -->
    <AlbumViewPermissions
      v-model="showViewModal"
      :event-id="eventId"
      :session-user-id="session.userId"
      @saved="onViewPermissionSaved"
    />
    <UploadPermissionsModal
      v-model="showUploadModal"
      :event-id="eventId"
      :session-user-id="session.userId"
      @saved="onUploadPermissionsSaved"
    />
    <ShareAlbumModal
      v-model="showShareModal"
      :event-id="eventId"
      :session-user-id="session.userId"
    />
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from 'vue'
import { useRoute } from 'vue-router'
import { useSessionStore } from '@/stores/session'
import api from '@/services/api'
import FileUploader from '@/components/common/FileUploader.vue'
import BackButton from '@/components/common/BackButton.vue'
import lightGallery from 'lightgallery'

import AlbumViewPermissions from '@/components/common/AlbumViewPermissions.vue'
import UploadPermissionsModal from '@/components/common/UploadPermissionsModal.vue'
import ShareAlbumModal from '@/components/common/ShareAlbumModal.vue'

/** ---- ROUTE / SESSION ---- */
const route = useRoute()
const session = useSessionStore()

/** ---- ROUTE PARSING ---- */
const eventIdFromRoute = (r) => {
  const p = r.params?.id ?? r.params?.slug
  if (p != null && String(p).trim() !== '') return String(p).trim()
  const last = (r.path || r.fullPath || '').split('?')[0].split('/').filter(Boolean).pop()
  return last && /^\d+$/.test(last) ? last : ''
}
const eventIdRef = ref(eventIdFromRoute(route))
watch(() => route.fullPath, () => { eventIdRef.value = eventIdFromRoute(route) })
const eventId = computed(() => eventIdRef.value)

const source = computed(() => String(route.query?.source || 'my').toLowerCase())
const isMySource = computed(() => source.value === 'my')

/** ---- STATE ---- */
const albums = ref([])
const myevent = ref(null)
const loading = ref(false)
const page = ref(1)
const hasMore = ref(true)

/** Selection & download state */
const mode = ref('view')
const selectedImages = ref([])
const deleting = ref(false)
const downloading = ref(false)
const showDownloadMenu = ref(false)
const downloadDropdownRef = ref(null)
const galleryWrapperRef = ref(null)
const galleryInstance = ref(null)

const qualityOptions = [
  { key: 'high', label: 'High (Original)' },
  { key: 'medium', label: 'Medium (1920px)' },
  { key: 'low', label: 'Low (1024px)' }
]
const hasSelectedItems = computed(() => mode.value === 'select' && selectedImages.value.length > 0)

/** NEW: Cohost status (from getInvitationDetail) */
const cohostAddStatus = ref(0) // 1 = accessible
const eventAccess = ref('pending')
const albumAccess = ref('pending')

/** ---- HELPERS ---- */
const imgKey = (a) => String(a?.id ?? a?.image ?? a?.thumb ?? '')
const canProcessUrl = (url) => {
  if (typeof window === 'undefined') return false
  try {
    const target = new URL(url, window.location.origin)
    return target.origin === window.location.origin
  } catch (e) {
    return false
  }
}
// const getUserId = (u) => String(u?.id ?? u?.user_id ?? u?.guest_id ?? u?.contact_id ?? u?.uid ?? '')

const isDeletedTrue = (v) =>
  v === true || v === 1 || v === '1' || (typeof v === 'string' && v.toLowerCase().trim() === 'true')

const normalizeAccessValue = (value, fallback = 'no') => {
  const str = (value ?? '').toString().trim().toLowerCase()
  if (str === 'yes' || str === 'no') return str
  return fallback
}

/** Fast lookup for deletable items */
const deletableSet = computed(() => {
  const s = new Set()
  for (const a of albums.value) {
    if (isDeletedTrue(a?.is_deleted)) s.add(imgKey(a))
  }
  return s
})

/** Only show uploader: my -> always; shared -> when photAccess == 1 for matching event */
const canUpload = computed(() => {
  if (isMySource.value) return true
  const v = myevent.value?.photAccess
  return Number(v) === 1
})

const hasAlbumRights = computed(() => eventAccess.value === 'yes' || albumAccess.value === 'yes')
const accessDenied = computed(() => eventAccess.value === 'no' || albumAccess.value === 'no')

/** ---- Title extraction ---- */
const pickTitle = (o) => {
  if (!o || typeof o !== 'object') return ''
  const direct =
    o.orgname || o.org_name
  if (direct) return String(direct)
  const ei = Array.isArray(o.eventsinfo) && o.eventsinfo.length ? o.eventsinfo[0] : null
  if (ei) {
    return String(
      ei.orgname || ei.org_name ||
      ei.eventname || ei.event_name ||
      ei.title || ei.name || ''
    )
  }
  return String(o.eventname || o.event_name || o.title || o.name || o.event_description || '')
}
const eventTitle = computed(() => pickTitle(myevent.value))

/** ---- API: PHOTOS ---- */
const fetchAlbumPhotos = async (pageNum = 1) => {
  if (loading.value) return
  loading.value = true
  const formData = new FormData()
  formData.append('user_id', session.userId)
  formData.append('event_id', eventId.value)
  formData.append('page', pageNum)
  try {
    const response = isMySource.value
      ? await api.getAlbumPrivate(formData)
      : await api.getAlbumPublic(formData)
    if (response?.status || response?.data?.status) {
      const data = response?.data?.data ?? response?.data ?? []
      const newAlbums = Array.isArray(data) ? data : []
      albums.value = pageNum === 1 ? newAlbums : [...albums.value, ...newAlbums]
      hasMore.value = newAlbums.length > 0
    } else {
      if (pageNum === 1) albums.value = []
      hasMore.value = false
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Error fetching public album:', e)
  } finally {
    loading.value = false
  }
}

const refreshAlbums = async (reset = false) => {
  if (reset) {
    page.value = 1
    hasMore.value = true
  }
  return fetchAlbumPhotos(page.value)
}

const imageSrc = (img) => img?.image || img?.thumb || ''

const flattenImages = (keys = null) => {
  const imgs = []
  for (const img of albums.value) {
    if (keys && !keys.has(imgKey(img))) continue
    const url = imageSrc(img)
    if (!url) continue
    const name = img.filename || img.original_name || `album-${eventId.value}-${imgKey(img)}.jpg`
    imgs.push({ url, name })
  }
  return imgs
}

const QUALITY_LIMITS = {
  high: null,
  medium: 1920,
  low: 1024
}

const toggleDownloadMenu = () => {
  if (!albums.value.length || downloading.value) return
  showDownloadMenu.value = !showDownloadMenu.value
}
const closeDownloadMenu = () => { showDownloadMenu.value = false }

const requestDownload = async (scope, quality) => {
  if (downloading.value) return
  const list = scope === 'all'
    ? flattenImages()
    : flattenImages(new Set(selectedImages.value))
  if (!list.length) {
    closeDownloadMenu()
    return
  }
  closeDownloadMenu()
  let chosenQuality = quality
  if (quality !== 'high' && list.some(item => !canProcessUrl(item.url))) {
    alert('Some images are hosted externally and cannot be optimized in-browser. Downloading originals instead.')
    chosenQuality = 'high'
  }
  await triggerDownload(list, chosenQuality)
}

const triggerDownload = async (list, quality = 'high') => {
  if (!list.length) return
  downloading.value = true
  let failures = 0
  try {
    for (const item of list) {
      try {
        await downloadSingle(item, quality)
      } catch (err) {
        failures += 1
        console.error('download error', err)
      }
    }
  } finally {
    downloading.value = false
    if (failures) alert(`Failed to download ${failures} file(s).`)
  }
}

const downloadSingle = async (item, quality) => {
  if (!canProcessUrl(item.url)) {
    directDownloadLink(item.url, appendQualitySuffix(item.name, 'high'))
    return true
  }
  const blob = await fetchImageBlob(item.url)
  const adjusted = await applyQuality(blob, quality)
  const href = URL.createObjectURL(adjusted)
  const link = document.createElement('a')
  link.href = href
  link.download = appendQualitySuffix(item.name, quality)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(href)
  return true
}

const directDownloadLink = (url, filename) => {
  const link = document.createElement('a')
  link.href = url
  link.target = '_blank'
  link.rel = 'noopener'
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const fetchImageBlob = async (url) => {
  const response = await fetch(url, { mode: 'cors' })
  if (!response.ok) throw new Error('Failed to fetch image')
  return await response.blob()
}

const applyQuality = async (blob, quality) => {
  const limit = QUALITY_LIMITS[quality]
  if (!limit || typeof window === 'undefined') return blob
  const img = await loadImageFromBlob(blob)
  if (!img.width || img.width <= limit) return blob
  const scale = limit / img.width
  const canvas = document.createElement('canvas')
  canvas.width = limit
  canvas.height = Math.round(img.height * scale)
  const ctx = canvas.getContext('2d')
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height)
  return await new Promise((resolve, reject) => {
    canvas.toBlob((newBlob) => {
      if (newBlob) resolve(newBlob)
      else reject(new Error('Unable to process image'))
    }, 'image/jpeg', 0.92)
  })
}

const loadImageFromBlob = (blob) => new Promise((resolve, reject) => {
  const url = URL.createObjectURL(blob)
  const image = new Image()
  image.onload = () => {
    URL.revokeObjectURL(url)
    resolve(image)
  }
  image.onerror = (err) => {
    URL.revokeObjectURL(url)
    reject(err)
  }
  image.src = url
})

const appendQualitySuffix = (name, quality) => {
  if (quality === 'high') return name
  const dot = name.lastIndexOf('.')
  if (dot === -1) return `${name}-${quality}`
  return `${name.slice(0, dot)}-${quality}${name.slice(dot)}`
}

/** ---- NORMALIZERS (EVENT) ---- */
function normalizeMyEventDetail(res) {
  const d = res?.data ?? res
  if (!d) return null
  const matchByEventId = (arr) =>
    arr.find(ev => String(ev.id ?? ev.eventid ?? ev.event_id) === String(eventId.value)) || arr[0] || null

  if (Array.isArray(d.eventsinfo) && d.eventsinfo.length) {
    const eventInfo = matchByEventId(d.eventsinfo)
    if (eventInfo) return { ...(eventInfo || {}), ...d }
  }

  if (d.data && !Array.isArray(d.data) && typeof d.data === 'object') return d.data

  if (Array.isArray(d.data)) {
    const found = matchByEventId(d.data)
    if (found) return found
  }

  if (typeof d === 'object') return d
  return null
}

function normalizeSharedEvent(res) {
  const d = res?.data ?? res
  if (d?.list && Array.isArray(d.list)) {
    const found = d.list.find(ev => String(ev.eventid ?? ev.id) === String(eventId.value))
    return found || null
  }
  return null
}

/** ---- API: EVENT PERMISSIONS ---- */
const fetchMyEventDetail = async () => {
  try {
    if (typeof api.getAlbumDetail === 'function') {
      const fd = new FormData()
      fd.append('user_id', session.userId)
      fd.append('event_id', eventId.value)
      fd.append('userid', session.userId)
      fd.append('eventid', eventId.value)
      const res = await api.getAlbumDetail(fd)
      const ev = normalizeMyEventDetail(res)
      myevent.value = ev
      if (myevent.value) return
    }
    const formData = new FormData()
    formData.append('id', session.userId)
    const res = await api.getAlbums(formData)
    myevent.value = normalizeMyEventDetail(res)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('API error fetchMyEventDetail:', e)
    myevent.value = null
  }
}

const fetchSharedEventAccess = async () => {
  try {
    const formData = new FormData()
    formData.append('id', session.userId)
    const res = await api.getReceivedAlbums(formData)
    myevent.value = normalizeSharedEvent(res)
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('API error fetchSharedEventAccess:', e)
    myevent.value = null
  }
}

/** NEW: Read cohost_add_status from getInvitationDetail to gate UI */
async function fetchCohostAddStatus() {
  try {
    const fd = new FormData()
    fd.append('userid', session.userId)
    fd.append('eventid', eventId.value)
    const res = await api.getInvitationDetail(fd)
    const payload = res?.data ?? res

    const direct = Number(
      payload?.cohost_add_status ??
      payload?.data?.cohost_add_status ??
      payload?.meta?.cohost_add_status ??
      0
    )
    cohostAddStatus.value = Number.isFinite(direct) ? direct : 0
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('fetchCohostAddStatus error:', e)
    cohostAddStatus.value = 0
  }
}


/** NEW: Read cohost_add_status from getInvitationDetail to gate UI */
async function fetchEventAccess() {
  try {
    eventAccess.value = 'pending'
    const fd = new FormData()
    fd.append('userid', session.userId)
    fd.append('eventid', eventId.value)
    const res = await api.getInvitationDetail(fd)
    const payload = res?.data ?? res ?? {}
    const eventAccessStatus =
      payload?.event_access ??
      payload?.data?.event_access ??
      payload?.meta?.event_access ??
      ''
    eventAccess.value = normalizeAccessValue(eventAccessStatus)

    const albumStatus =
      payload?.album_access ??
      payload?.data?.album_access ??
      payload?.meta?.album_access ??
      ''
    if (albumStatus !== '') {
      albumAccess.value = normalizeAccessValue(albumStatus, 'pending')
    }
  } catch (e) {
    console.error('fetchEventAccess error:', e)
    eventAccess.value = 'no'
  }
}

async function fetchAlbumAccess () {
  try {
    albumAccess.value = 'pending'
    const fd = new FormData()
    fd.append('eventid', eventId.value)
    fd.append('userid', session.userId)
    const res = await api.checkISHost(fd)
    const payload = res?.data || res || {}
    const data = payload?.data || payload
    albumAccess.value = normalizeAccessValue(data?.album_access, 'no')
  } catch (e) {
    console.error('fetchAlbumAccess error:', e)
    albumAccess.value = 'no'
  }
}



/** ---- INFINITE SCROLL ---- */
const handleScroll = () => {
  if (showDownloadMenu.value) closeDownloadMenu()
  if (loading.value || !hasMore.value) return
  const threshold = 200
  const scrolled = window.innerHeight + window.pageYOffset
  const full = document.documentElement.scrollHeight
  if (full - scrolled < threshold) {
    page.value += 1
    fetchAlbumPhotos(page.value)
  }
}

const handleDocumentClick = (e) => {
  if (!showDownloadMenu.value) return
  if (!downloadDropdownRef.value) return
  if (!downloadDropdownRef.value.contains(e.target)) closeDownloadMenu()
}

/** ---- LIFECYCLE ---- */
const boot = async () => {
  albums.value = []
  myevent.value = null
  page.value = 1
  hasMore.value = true
  eventAccess.value = 'pending'
  albumAccess.value = 'pending'

  await fetchAlbumPhotos(1)

  if (isMySource.value) {
    await fetchMyEventDetail()
  } else {
    await fetchSharedEventAccess()
  }

  // cohost gate
  await fetchCohostAddStatus()
  await fetchEventAccess()
  if (albumAccess.value === 'pending') {
    await fetchAlbumAccess()
  }
  nextTick(() => initLightGallery())
}

onMounted(() => {
  window.addEventListener('scroll', handleScroll, { passive: true })
  document.addEventListener('click', handleDocumentClick)
  boot()
})
onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll)
  document.removeEventListener('click', handleDocumentClick)
  destroyGallery()
})

watch(() => [eventId.value, source.value], () => boot())

/** ---- MODE / SELECT / DELETE ---- */
function toggleSelectMode() {
  mode.value = mode.value === 'select' ? 'view' : 'select'
  if (mode.value !== 'select') selectedImages.value = []
}

function buildDeletePayload() {
  const chosen = new Set(selectedImages.value.filter(k => deletableSet.value.has(k)))
  const ids = []
  const paths = []
  for (const a of albums.value) {
    const key = imgKey(a)
    if (!chosen.has(key)) continue
    if (a?.id != null) ids.push(String(a.id))
    if (a?.photo_delete) paths.push(String(a.photo_delete))
  }
  return { ids, file_paths: paths }
}

async function deleteSelected() {
  const effective = selectedImages.value.filter(k => deletableSet.value.has(k))
  if (!effective.length || deleting.value) return
  if (!confirm(`Delete ${effective.length} selected image(s)?`)) return
  try {
    deleting.value = true
    const fd = new FormData()
    fd.append('user_id', session.userId)
    fd.append('event_id', eventId.value)
    const payload = buildDeletePayload()
    if (!payload.ids.length && !payload.file_paths.length) {
      deleting.value = false
      return
    }
    if (payload.ids.length) fd.append('ids', payload.ids.join(','))
    if (payload.file_paths.length) fd.append('file_path', payload.file_paths.join(','))

    const res = await api.deleteAlbumImages(fd)
    const ok = res?.status === true || res?.data?.status === true || res?.data?.status === 'ok'
    if (ok) {
      const removeSet = new Set(effective)
      albums.value = albums.value.filter(a => !removeSet.has(imgKey(a)))
      selectedImages.value = []
      if (albums.value.length === 0) page.value = 1
    } else {
      alert('Failed to delete selected images.')
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('deleteSelected error:', e)
    alert('Error deleting images.')
  } finally {
    deleting.value = false
  }
}

/** --- Select-mode helpers to disable Fancybox and toggle selection --- */
function isKeySelected(key) { return selectedImages.value.includes(key) }
function onImageClick(img, e, index) {
  if (mode.value !== 'select') {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    openLightbox(index)
    return
  }
  e?.preventDefault?.()
  e?.stopPropagation?.()

  const key = imgKey(img)
  if (!deletableSet.value.has(key)) return // only togglable if deletable

  const list = new Set(selectedImages.value)
  if (list.has(key)) list.delete(key)
  else list.add(key)
  selectedImages.value = Array.from(list)
}

/** ---- Modal toggles (Vue-driven) ---- */
const showViewModal = ref(false)
const showUploadModal = ref(false)
const showShareModal = ref(false)

function openViewPerms(){ showViewModal.value = true }
function openUploadPerms(){ showUploadModal.value = true }
function openShare(){ showShareModal.value = true }

const destroyGallery = () => {
  if (galleryInstance.value) {
    try {
      galleryInstance.value.destroy(true)
    } catch (err) {
      console.warn('LightGallery destroy failed', err)
    }
    galleryInstance.value = null
  }
}

const initLightGallery = async () => {
  if (!galleryWrapperRef.value || !albums.value.length || mode.value === 'select') return
  await nextTick()
  destroyGallery()
  galleryInstance.value = lightGallery(galleryWrapperRef.value, {
    selector: '.imgBlock-inner',
    download: false
  })
}

const openLightbox = async (index) => {
  if (mode.value === 'select') return
  if (!galleryInstance.value) await initLightGallery()
  if (galleryInstance.value) galleryInstance.value.openGallery(index)
}

function onViewPermissionSaved(mode){
  if (myevent.value) myevent.value.type_event = (mode === 'private' ? 'private' : 'public')
}
function onUploadPermissionsSaved(){
  // hook if you want to refresh anything after saving
}

/** ---- WATCHERS ---- */
watch(albums, () => {
  const filtered = selectedImages.value.filter(k => deletableSet.value.has(k))
  if (
    filtered.length !== selectedImages.value.length ||
    filtered.some((v, i) => v !== selectedImages.value[i])
  ) {
    selectedImages.value = filtered
  }
  nextTick(() => initLightGallery())
})

watch(mode, () => { closeDownloadMenu() })
watch(mode, (val) => {
  if (val === 'select') destroyGallery()
  else nextTick(() => initLightGallery())
})
</script>

<style>
.page-bar{
  position: sticky;
  top: 0;
  z-index: 1000;
  background: #fff;
  border-bottom: 1px solid rgba(0,0,0,.06);
  margin-bottom: 12px;
}

.page-title{
  color:#2a1b22;
  font-size:1.08rem;
  font-weight:700;
}

.dot-btn{
  width:34px;
  height:34px;
  padding:0;
  border:1px solid #f1d7e1;
  border-radius:999px;
  background:#fff;
  color:#654654;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  box-shadow:0 6px 16px rgba(236,72,153,.08);
}

.album-detail-menu{
  min-width:220px;
  margin-top:10px;
  border:1px solid #f3d4df;
  border-radius:14px;
  padding:6px;
  box-shadow:0 14px 30px rgba(86, 42, 62, 0.16);
}

.album-detail-menu .dropdown-divider{
  margin:.35rem 0;
  border-top:1px solid #f3d4df;
}

.album-detail-menu__item{
  display:flex;
  align-items:center;
  gap:10px;
  border-radius:10px;
  padding:9px 10px;
  color:#3b2230;
  font-size:.92rem;
  font-weight:600;
}

.album-detail-menu__item i{
  width:16px;
  text-align:center;
  color:#ec4899;
}

.album-detail-menu__item:hover,
.album-detail-menu__item:focus{
  background:#fff4f8;
  color:#3b2230;
}

.album-detail-menu__item--danger{
  color:#be123c;
}

.album-detail-menu__item--danger i{
  color:#be123c;
}

.detail-card{
  border:1px solid #f2d5df;
  border-radius:22px;
  background:linear-gradient(180deg, #fff8fb 0%, #fff2f7 100%);
  box-shadow:0 10px 24px rgba(242, 172, 196, 0.14);
}

.detail-card--compact{
  overflow:hidden;
}

.detail-card__head{
  padding:14px 16px 0;
}

.detail-card__body{
  padding:16px;
}

/* Masonry */
.masonry { columns: 4; column-gap: 16px; }
@media (max-width: 992px){ .masonry { columns: 3; } }
@media (max-width: 768px){ .masonry { columns: 2; } }
@media (max-width: 576px){ .masonry { columns: 2; } }
.masonry-item { break-inside: avoid; margin-bottom: 16px; }

.gallery-header{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:1rem;
  flex-wrap:wrap;
}

.gallery-title{
  color:#2b1b22;
  font-size:1.02rem;
  font-weight:700;
}

.gallery-toolbar{
  display:flex;
  flex-wrap:wrap;
  justify-content:flex-end;
  gap:.5rem;
}

.toolbar-btn{
  min-width:136px;
  border:1px solid #f0c8d7;
  background:#fff;
  color:#7a4a5f;
  border-radius:999px;
  padding:10px 14px;
  font-weight:700;
  display:inline-flex;
  align-items:center;
  justify-content:center;
  gap:.35rem;
}

.toolbar-btn.active{
  background:#fff4f8;
  color:#b8326d;
  box-shadow:0 6px 16px rgba(236,72,153,.14);
}

.toolbar-btn--muted{
  color:#654654;
}

.toolbar-btn--danger{
  border-color:#fecdd3;
  color:#be123c;
}

.download-dropdown{
  position:relative;
}
.download-menu{
  position:absolute;
  right:0;
  top:calc(100% + 8px);
  width:240px;
  background:#fff;
  border:1px solid #f3d4df;
  border-radius:14px;
  padding:.6rem;
  z-index:105;
  box-shadow:0 14px 30px rgba(86, 42, 62, 0.16);
}
.download-label{
  font-size:.78rem;
  font-weight:700;
  color:#8c6f7c;
  text-transform:uppercase;
  letter-spacing:.05em;
  margin-bottom:.25rem;
}
.download-menu-item{
  width:100%;
  border:0;
  border-radius:10px;
  background:#fff8fb;
  color:#3b2230;
  padding:.45rem .65rem;
  text-align:left;
  font-weight:600;
  margin-bottom:.35rem;
  transition:background .15s ease;
}
.download-menu-item:hover{
  background:#fff0f6;
}
.download-note{
  font-size:.8rem;
  color:#a78b98;
  margin-top:.35rem;
}

.imgBlock{
  background:linear-gradient(180deg, #fff6fa 0%, #ffeaf3 100%);
  border:1px solid #f4cfdb;
  border-radius:16px;
  padding:8px;
  box-shadow:0 8px 18px rgba(242, 172, 196, 0.16);
  position:relative;
  overflow:hidden;
}
.imgBlock-bg{
  position:absolute;
  inset:0;
  background-size:cover;
  background-position:center;
  filter:blur(24px) saturate(1.1);
  transform:scale(1.08);
  opacity:0;
  transition:opacity .2s ease;
}
.imgBlock::after{
  content:'';
  position:absolute;
  inset:0;
  background:linear-gradient(135deg, rgba(58, 19, 36, .42), rgba(58, 19, 36, .08));
  opacity:0;
  transition:opacity .2s ease;
  z-index:0;
}
.imgBlock .album-img{
  width:100%;
  height:100%;
  object-fit:cover;
  display:block;
  border-radius:10px;
  position:relative;
  z-index:1;
}
.imgBlock-inner{
  position:relative;
  border-radius:10px;
  overflow:hidden;
}
.imgBlock-overlay{
  position:absolute;
  inset:0;
  border:0;
  background:linear-gradient(180deg, rgba(58,19,36,.12), rgba(58,19,36,.45));
  color:#fff;
  display:flex;
  align-items:center;
  justify-content:center;
  opacity:0;
  transition:opacity .2s ease;
  font-size:1.4rem;
  cursor:pointer;
}
.imgBlock:hover .imgBlock-overlay{
  opacity:1;
}
.imgBlock i { color: #fff; }
.imgBlock h4 {
  font-size:10px;
  margin-top:8px;
  margin-bottom:5px;
  color:#7a4a5f;
  position:relative;
  z-index:2;
}

@media (max-width: 992px){
  .gallery-header{
    flex-direction:column;
    align-items:flex-start;
  }
  .gallery-toolbar{
    width:100%;
    flex-direction:column;
  }
  .toolbar-btn{
    width:100%;
  }
  .download-dropdown,
  .toolbar-btn{
    width:100%;
  }
  .download-menu{
    position:static;
    width:100%;
    margin-top:.5rem;
  }
  .imgBlock{
    min-height:220px;
  }
  .imgBlock-bg,
  .imgBlock::after{
    opacity:1;
  }
  .imgBlock .album-img{
    object-fit:contain;
    padding:12px;
  }
}
@media (max-width: 576px){
  .imgBlock{
    min-height:180px;
  }
  .imgBlock .album-img{
    padding:10px;
  }
}

.image-checkbox {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 10;
  display: inline-flex;
  width: 24px;
  height: 24px;
}
.image-checkbox input {
  display: none;
}
.image-checkbox .checkmark {
  width: 100%;
  height: 100%;
  border-radius: 6px;
  border: 2px solid rgba(255,255,255,0.85);
  background: rgba(15,23,42,0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  transition: all 0.15s ease;
}
.image-checkbox input:checked + .checkmark {
  background: #0d6efd;
  border-color: #0d6efd;
  box-shadow: 0 4px 12px rgba(13,110,253,0.45);
}
.image-checkbox input:checked + .checkmark::after {
  content: '✓';
}

/* fallback modal visibility when no Bootstrap JS */
.modal.show { display:block; }

/* Custom Tabs (used in child components) */
.tabset{
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #e9ecef;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}
.tab-btn{
  appearance: none;
  border: 0;
  background: transparent;
  padding: .5rem .75rem;
  border-radius: .25rem .25rem 0 0;
  font-weight: 600;
  color: #6c757d;
  cursor: pointer;
}
.tab-btn.active{
  color: #212529;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-bottom-color: transparent;
}

/* Small fix for dropdown overlap on mobile */
.dropdown-menu { z-index: 1050; }

/* Skeleton loader */
.skeleton-card{ border-radius:.5rem; box-shadow:0 1px 10px rgba(0,0,0,.07); }
.skeleton-line,
.skeleton-block{
  position:relative; overflow:hidden; background:#e9ecef;
  border-radius:.35rem;
}
.skeleton-line{ height:14px; }
.skeleton-line--lg{ height:22px; width:70%; }
.skeleton-line--md{ width:55%; }
.skeleton-line--sm{ width:40%; }
.skeleton-block{ height:140px; border-radius:.5rem; }
.skeleton-line::after,
.skeleton-block::after{
  content:""; position:absolute; inset:0;
  background:linear-gradient(90deg, transparent, rgba(255,255,255,.6), transparent);
  transform:translateX(-100%);
  animation:skeleton-glow 1.5s ease-in-out infinite;
}
@keyframes skeleton-glow{
  0%{ transform:translateX(-100%); }
  100%{ transform:translateX(100%); }
}
</style>
